import * as React from 'react'

import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import { AttendanceCustomTimeRangeDayEnum } from 'generated/resources/graphql'
import type { SettingPageCustomTimeRangeFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { useCustomTimeRange } from 'modules/attendance/hooks/useCustomTimeRange'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import CustomTimeRangeUtils from 'modules/attendance/lib/utils/CustomTimeRangeUtils'

import Block from './Block'
import Cell from './Cell'

interface Props {
  dayTimeRanges?: SettingPageCustomTimeRangeFragment[]
  dateRangesByDate?: Record<string, SettingPageCustomTimeRangeFragment[]>
  sortedRangeDates?: string[]
  showSpecificDatesColumn?: boolean
}

const TableBody = ({
  dayTimeRanges: providedRanges,
  dateRangesByDate = {},
  sortedRangeDates = [],
  showSpecificDatesColumn = false,
}: Props) => {
  const { dayEnums } = useCustomTimeRange()
  const { customTimeRanges: contextRanges } = useAttendanceSettingWorkCategories()
  const customTimeRanges = providedRanges ?? contextRanges

  const timeRangesByStartTime = CustomTimeRangeUtils.timeRangesByStartTime(customTimeRanges)
  const sortedStartTimes = CustomTimeRangeUtils.sortedStartTimes(timeRangesByStartTime)
  const hasCustomTypeTimeRanges = CustomTimeRangeUtils.hasCustomTypeTimeRanges(customTimeRanges)
  const hasBaseTypeTimeRanges = CustomTimeRangeUtils.hasBaseTypeTimeRanges(customTimeRanges)
  const hasTimeRanges = CustomTimeRangeUtils.hasTimeRanges(customTimeRanges)
  const baseTypeTimeRanges = CustomTimeRangeUtils.baseTypeTimeRanges(customTimeRanges)
  const timeRangesByType = CustomTimeRangeUtils.getAllTimeRangesByType(baseTypeTimeRanges)
  const hasSpecificTimeRanges = sortedRangeDates.length > 0

  const renderSpecificDatesCell = (isFirstRow: boolean) => {
    if (!showSpecificDatesColumn) {
      return null
    }

    if (!hasSpecificTimeRanges && !isFirstRow) {
      return (
        <Table.Cell key="specific_empty" verticalAlign="top">
          <Box padding="s12" />
        </Table.Cell>
      )
    }

    if (!isFirstRow) {
      return <Table.Cell key="specific_spacer" />
    }

    if (!hasSpecificTimeRanges) {
      return (
        <Table.Cell key="specific_not_defined" verticalAlign="top">
          <Box padding="s12">
            <Text.Normal size="200" color="grey600">
              {i18n.t('time_tracking.settings.custom_time_range_section.not_defined')}
            </Text.Normal>
          </Box>
        </Table.Cell>
      )
    }

    return (
      <Table.Cell key="specific_dates" verticalAlign="top">
        <Box gap="s12" padding="s12" minWidth="s240">
          {sortedRangeDates.map((date) => {
            const ranges = dateRangesByDate[date] ?? []
            return ranges.map((range) => (
              <Block
                key={`${date}_${range.id}`}
                timeRanges={[range]}
                base={CustomTimeRangeUtils.isBase(range)}
              />
            ))
          })}
        </Box>
      </Table.Cell>
    )
  }

  const renderEmptyRow = () => (
    <Table.Row background="grey100">
      {renderSpecificDatesCell(true)}
      {dayEnums.map((day) => (
        <Table.Cell key={`empty_${day}`}>
          <Cell timeRanges={[]} dayType={day} base showNotDefined />
        </Table.Cell>
      ))}
    </Table.Row>
  )

  if (!hasTimeRanges) {
    return <Table.Body>{renderEmptyRow()}</Table.Body>
  }

  const renderBaseTypeRows = () =>
    Object.entries(timeRangesByType).map(([timeType, rangesByType], index) => {
      if (rangesByType.length === 0) return null
      return (
        <Table.Row key={timeType} background="grey100">
          {renderSpecificDatesCell(index === 0)}
          {dayEnums.map((day: AttendanceCustomTimeRangeDayEnum) => (
            <Table.Cell key={`${timeType}_${day}`}>
              <Cell
                timeRanges={CustomTimeRangeUtils.rangesInSpecificBaseDay(rangesByType, day)}
                dayType={day}
                base
                showNotDefined={
                  index === 0 && !CustomTimeRangeUtils.hasRangesInSpecificDay(customTimeRanges, day)
                }
              />
            </Table.Cell>
          ))}
        </Table.Row>
      )
    })

  const renderCustomTypeRows = () =>
    sortedStartTimes.map((startTime, index) => (
      <Table.Row key={startTime} background="grey100">
        {renderSpecificDatesCell(!hasBaseTypeTimeRanges && index === 0)}
        {dayEnums.map((day) => (
          <Table.Cell key={`time_slot_${day}`} verticalAlign="top">
            <Box paddingTop={!hasBaseTypeTimeRanges && index === 0 ? 's12' : undefined}>
              <Cell
                timeRanges={CustomTimeRangeUtils.rangesInSpecificCustomDay(
                  timeRangesByStartTime[startTime] ?? [],
                  day
                )}
                dayType={day}
                showNotDefined={
                  !hasBaseTypeTimeRanges &&
                  index === 0 &&
                  !CustomTimeRangeUtils.hasRangesInSpecificDay(customTimeRanges, day)
                }
              />
            </Box>
          </Table.Cell>
        ))}
      </Table.Row>
    ))

  return (
    <Table.Body>
      {hasBaseTypeTimeRanges && renderBaseTypeRows()}
      {hasCustomTypeTimeRanges && renderCustomTypeRows()}
    </Table.Body>
  )
}

export default TableBody
